import { Component, OnDestroy, OnInit } from '@angular/core';
import { ModalRef } from '@purespectrum1/ui/modal';
import { Subscription } from 'rxjs';
import {
  TemplateListResponse,
  TemplateInfo,
  SelectedTemplate,
  ICreateFolder,
  TemplateFolder,
  TemplateSupplierData,
} from './template-modal.interface';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TemplateService } from '../../../shared/services/template/template.service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { notifyMessage } from '../../../shared/constants/notify-message';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { SurveyMetaDataService } from '../../../shared/services/survey-meta-data/survey-meta-data.service';
import { TreeDropdownOption } from '../../shared/interfaces/tree-dropdown-interface';
import { LayoutConstants } from '../layout-constants';

@Component({
  selector: 'ps-ui-template-modal',
  templateUrl: './template-modal.component.html',
  styleUrls: ['./template-modal.component.css'],
})
export class TemplateModalComponent implements OnInit, OnDestroy {
  public readonly MAX_NAVIGATION_DEPTH =
    LayoutConstants.TEMPLATE_MODAL.FOLDER_DROPDOWN.MAX_NAVIGATION_DEPTH;

  private _subscriptions: Subscription = new Subscription();

  public templateList: TemplateListResponse[] = [];
  public templates: TemplateInfo[] = [];
  public selectedTemplate?: TemplateInfo;
  public selectedFolder = <TemplateListResponse>{};
  public selectedParent = <TemplateListResponse>{};
  public form!: FormGroup;
  public templateFolderOption: TreeDropdownOption = {
    uniqueKey: 'id',
    displayKey: 'name',
    navigationKey: 'subfolders',
    headerLabel: 'My templates',
    removeItem: false,
    icon: 'fa fa-folder',
    noContentMsg: 'No Folder created yet', // Even though it is template-folder content, this message is for ease of user
    mcIconIdentifier: '',
  };
  public isMultiCountrySurvey: boolean = false;

  constructor(
    private _fb: FormBuilder,
    private _modalRef: ModalRef<
      TemplateModalComponent,
      SelectedTemplate | undefined
    >,
    private _templateService: TemplateService,
    private _toastr: ToasterService,
    private _authService: AuthService,
    private _surveyMetaDataService: SurveyMetaDataService
  ) {}

  ngOnInit(): void {
    this.getTemplateList();
    this._initForm();
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSurveyData()
        .subscribe((surveyResponse) => {
          if (surveyResponse && surveyResponse.mcUniqueId) {
            this.isMultiCountrySurvey = true;
            this.form.patchValue({
              is_mc_template: true,
            });
          }
        })
    );
  }
  private _initForm() {
    this.form = this._fb.group({
      selectedFolder: [null],
      templateName: [null, [Validators.required]],
      is_suppliers_list: [false],
      is_mc_template: [false],
    });
  }

  getTemplateList() {
    const fetchSelfOwnedTemplates = true;
    this._subscriptions.add(
      this._templateService
        .allTemplates(fetchSelfOwnedTemplates)
        .subscribe((response: TemplateListResponse[]) => {
          this.templateList = response;
        })
    );
  }

  get companyId() {
    return this._authService.isLoggedInAsServiceBuyer()
      ? this._authService.serviceBuyer?.id
      : this._authService.user?.cmp;
  }

  get showCreateFolderButton() {
    // Either it is root folder, or if it is subfolder then the user should be able to access it
    return !this.selectedFolder.id || this.canCreateNewFolder;
  }

  get canCreateNewFolder() {
    return (
      this.selectedFolder.is_service_buyer ===
        this._authService.isLoggedInAsServiceBuyer() &&
      this.selectedFolder.owner === this.companyId
    );
  }

  selectFolder({
    folder,
    parent,
  }: {
    folder: TemplateListResponse;
    parent: TemplateListResponse;
  }) {
    this.selectedFolder = folder;
    this.selectedParent = parent;
    this.templates = this.selectedFolder.templates || [];
    this.form.controls.templateName.patchValue(null);
    this.selectedTemplate = undefined;
  }

  unsetSelectedFolder() {
    this.selectedFolder = this.selectedParent = <TemplateListResponse>{};
    this.form.controls.selectedFolder.patchValue(null);
  }

  createNewFolder(folderName: string) {
    const payload: ICreateFolder = {
      name: folderName,
      is_service_buyer: this._authService.isLoggedInAsServiceBuyer(),
      parent_folder: this.selectedFolder.id,
    };
    this._templateService.createFolder(payload).subscribe(
      (resp) => {
        this._addNewFolderInFolderList(resp);
        this._toastr.success(
          notifyMessage.successMessage.TEMPLATES.TEMPLATE_FOLDER_SAVED
        );
      },
      (err) => {
        this._toastr.error(err.error.ps_api_response_message);
      }
    );
  }

  private _addNewFolderInFolderList(newFolder: TemplateFolder) {
    const newItem: TemplateListResponse = {
      ...newFolder,
      templates: [],
    };
    const foundParentFolder = this.templateList.find(
      (item) => item.id === this.selectedFolder.id
    );
    if (!foundParentFolder) {
      this.templateList.push(newItem);
      return;
    }
    foundParentFolder.subfolders = [
      ...(foundParentFolder.subfolders || []),
      newItem,
    ];
  }

  createNewTemplate(template: string) {
    const tempTemplate = {
      owner: this.companyId ?? 0,
      shared_with: [],
      name: template,
      is_mc_template: this.isMultiCountrySurvey,
      is_owner: true,
      is_service_buyer: this._authService.isLoggedInAsServiceBuyer(),
    };
    this.templates = [...this.templates, tempTemplate];

    // selecting the created template
    this.selectTemplate(tempTemplate);
  }

  selectTemplate(template: TemplateInfo) {
    this.selectedTemplate = template;
    this.setTemplateToggles(template);
    if (this.isMultiCountrySurvey) {
      this.setMultiCountryTemplate(template);
    }
  }

  close() {
    this._modalRef.close();
  }

  save() {
    if (!this.selectedTemplate?.id && !this.canCreateNewFolder) {
      return this._toastr.error(
        notifyMessage.errorMessage.TEMPLATES
          .TEMPLATE_ADDITION_NOT_ALLOWED_IN_FOLDER
      );
    }
    if (
      this.selectedTemplate?.owner !== this.companyId ||
      (this._authService.isLoggedInAsServiceBuyer() &&
        !this.selectedTemplate?.is_service_buyer)
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.TEMPLATES.TEMPLATE_MODIFICATION_NOT_ALLOWED
      );
      return;
    }
    this._modalRef.close({
      folder: this._templateService.prepareTemplateFolderData(
        this.selectedFolder
      ),
      template: this.selectedTemplate!,
      ...this._fetchTemplateOptions(),
    });
  }

  private _fetchTemplateOptions() {
    const { is_suppliers_list = false, is_mc_template = false } =
      this.form.value;
    return {
      is_suppliers_list,
      is_mc_template,
      suppliers_data: <TemplateSupplierData>{},
    };
  }

  patchFormValue<T>(key: string, value: T) {
    this.form.patchValue({
      [key]: value,
    });
  }

  setTemplateToggles(template: TemplateInfo) {
    const { is_suppliers_list } = template;
    this.form.patchValue({
      is_suppliers_list,
    });
  }

  setMultiCountryTemplate(template: TemplateInfo) {
    this.patchFormValue('is_mc_template', template.is_mc_template);
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
